import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, UserPlus, Loader2, Trash2, Mail, ShieldCheck, Shield,
  Power, PowerOff, X, RefreshCcw, Send, KeyRound,
} from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { master_admin: "Master-Admin", admin: "Admin" };
const STATUS_META = {
  active: { label: "Aktiv", cls: "text-[#B8CFA0] border-[#B8CFA0]/40 bg-[#B8CFA0]/10" },
  pending: { label: "Einladung offen", cls: "text-[#F4C572] border-[#F4C572]/40 bg-[#F4C572]/10" },
  disabled: { label: "Deaktiviert", cls: "text-[#E27D7D] border-[#E27D7D]/40 bg-[#E27D7D]/10" },
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(() => axios.create({
    baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 15000,
  }), [token]);

  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);

  const isMaster = me?.role === "master_admin";

  const handleAuthErr = useCallback((e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 403 && detail === "PASSWORD_CHANGE_REQUIRED") {
      toast.warning("Bitte ändere zuerst dein Passwort.");
      navigate("/admin/leads", { replace: true });
      return true;
    }
    if (status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      navigate("/admin/login", { replace: true });
      return true;
    }
    if (status === 403) {
      toast.error(detail || "Dafür fehlt dir die Berechtigung.");
      return true;
    }
    return false;
  }, [navigate]);

  const load = useCallback(async () => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    setLoading(true);
    try {
      const meRes = await api.get("/admin/me");
      setMe(meRes.data);
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Laden fehlgeschlagen.");
    } finally { setLoading(false); }
  }, [api, token, navigate, handleAuthErr]);

  useEffect(() => { document.title = "CRM · Admin-Verwaltung"; load(); }, []); // eslint-disable-line

  const del = async (u) => {
    if (!window.confirm(`Admin "${u.username}" wirklich löschen? Der Zugang wird sofort gesperrt.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Admin gelöscht.");
      load();
    } catch (e) { if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen."); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Zugang deaktiviert." : "Zugang aktiviert.");
      load();
    } catch (e) { if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Änderung fehlgeschlagen."); }
  };

  const resend = async (u) => {
    try {
      const r = await api.post(`/admin/users/${u.id}/resend-invite`);
      if (r.data.invite_sent) toast.success("Einladung erneut gesendet.");
      else toast.warning(`Einladung erstellt, aber E-Mail nicht gesendet: ${r.data.invite_error || "E-Mail-Dienst nicht konfiguriert."}`);
    } catch (e) { if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-users-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-2">
              <ArrowLeft size={12} /> CRM
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Admin-Verwaltung</h1>
            <p className="text-[#A8A58F] text-sm mt-1">
              {isMaster
                ? "Admin-Zugänge direkt anlegen, Passwörter setzen, bearbeiten und deaktivieren."
                : "Weitere Admin-Zugänge direkt mit Passwort anlegen."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-4 py-2 text-sm" data-testid="users-refresh-btn">
              <RefreshCcw size={14} /> Neu laden
            </button>
            <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-full px-5 py-2.5 text-sm" data-testid="create-admin-btn">
              <UserPlus size={14} /> Admin anlegen
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-[#C9C6B4] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Lade…</div>
        ) : (
          <div className="space-y-3" data-testid="users-list">
            {users.map((u) => {
              const sm = STATUS_META[u.status] || STATUS_META.active;
              const uIsMaster = u.role === "master_admin";
              const isSelf = me && me.id === u.id;
              return (
                <div key={u.id} className="glass-strong rounded-2xl p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`user-row-${u.username}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2">
                      {uIsMaster ? <ShieldCheck size={16} className="text-[#E8E5D0]" /> : <Shield size={16} className="text-[#A8A58F]" />}
                      <span className="font-display font-black text-[#F3F1E8]">{u.username}</span>
                      {isSelf && <span className="text-[10px] uppercase tracking-wider text-[#A8A58F] border border-white/15 rounded-full px-2 py-0.5">Du</span>}
                    </div>
                    <div className="text-sm text-[#C9C6B4] mt-1 flex items-center gap-1.5"><Mail size={12} /> {u.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] border border-white/15 rounded-full px-3 py-1">{ROLE_LABELS[u.role] || u.role}</span>
                    <span className={`text-[11px] font-bold uppercase tracking-wider border rounded-full px-3 py-1 ${sm.cls}`} data-testid={`user-status-${u.username}`}>{sm.label}</span>
                  </div>
                  {/* Management actions are master-only (also enforced server-side) */}
                  {isMaster && (
                    <div className="flex items-center gap-1.5">
                      {u.status === "pending" && (
                        <button onClick={() => resend(u)} title="Einladung erneut senden" className="p-2 rounded-lg border border-white/12 hover:bg-black/[0.15]" data-testid={`resend-${u.username}`}><Send size={14} /></button>
                      )}
                      <button onClick={() => setPwUser(u)} title="Passwort setzen" className="p-2 rounded-lg border border-white/12 hover:bg-black/[0.15]" data-testid={`setpw-${u.username}`}><KeyRound size={14} /></button>
                      <button onClick={() => setEditUser(u)} title="Bearbeiten" className="p-2 rounded-lg border border-white/12 hover:bg-black/[0.15]" data-testid={`edit-${u.username}`}><Shield size={14} /></button>
                      {u.status !== "pending" && (
                        <button onClick={() => toggleActive(u)} title={u.active ? "Deaktivieren" : "Aktivieren"} className="p-2 rounded-lg border border-white/12 hover:bg-black/[0.15]" data-testid={`toggle-active-${u.username}`}>
                          {u.active ? <PowerOff size={14} className="text-[#E27D7D]" /> : <Power size={14} className="text-[#B8CFA0]" />}
                        </button>
                      )}
                      <button onClick={() => del(u)} title="Löschen" className="p-2 rounded-lg border border-white/12 hover:bg-black/[0.15]" data-testid={`delete-${u.username}`}><Trash2 size={14} className="text-[#E27D7D]" /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && <CreateUserModal api={api} isMaster={isMaster} handleAuthErr={handleAuthErr} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}
      {editUser && <EditUserModal api={api} user={editUser} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} />}
      {pwUser && <SetPasswordModal api={api} user={pwUser} handleAuthErr={handleAuthErr} onClose={() => setPwUser(null)} onDone={() => { setPwUser(null); load(); }} />}
    </div>
  );
};

const CreateUserModal = ({ api, isMaster, handleAuthErr, onClose, onDone }) => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("admin");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password.trim().length < 8) { toast.error("Das Passwort muss mindestens 8 Zeichen haben."); return; }
    setLoading(true);
    try {
      const body = { username: username.trim(), email: email.trim(), password: password };
      if (isMaster) body.role = role;
      const r = await api.post("/admin/users", body);
      toast.success(`Admin "${r.data.user.username}" angelegt. Der Zugang ist sofort aktiv.`);
      onDone();
    } catch (e) { if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Erstellen fehlgeschlagen."); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-[#5C6B49] border border-white/15 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="create-user-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-black">Admin anlegen</h2>
          <button type="button" onClick={onClose} className="p-1 text-[#C9C6B4] hover:text-[#F3F1E8]"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] mb-1 block">Benutzername</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} required placeholder="z. B. tobi.nunner" autoComplete="off"
              className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] placeholder:text-[#A8A58F]" data-testid="create-username" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] mb-1 block">E-Mail-Adresse</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="name@domain.de" autoComplete="off"
              className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] placeholder:text-[#A8A58F]" data-testid="create-email" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] mb-1 block">Passwort</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="Mind. 8 Zeichen" autoComplete="new-password"
              className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] placeholder:text-[#A8A58F]" data-testid="create-password" />
          </div>
          {isMaster && (
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] mb-1 block">Rolle</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-[#4E5A3B] border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="create-role">
                <option value="admin">Admin</option>
                <option value="master_admin">Master-Admin</option>
              </select>
            </div>
          )}
          <p className="text-[11px] text-[#A8A58F]">
            {isMaster
              ? "Der Zugang ist sofort nutzbar. Das Passwort wird sicher verschlüsselt gespeichert."
              : "Du legst einen normalen Admin an. Der Zugang ist sofort nutzbar."}
          </p>
        </div>
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 hover:bg-black/[0.15] text-sm font-bold rounded-lg px-4 py-2">Abbrechen</button>
          <button type="submit" disabled={loading} className="flex-1 bg-[#E8E5D0] text-[#3A4230] text-sm font-bold rounded-lg px-4 py-2 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-submit">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Anlegen
          </button>
        </div>
      </form>
    </div>
  );
};

const EditUserModal = ({ api, user, onClose, onDone }) => {
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {};
      if (email.trim() !== user.email) body.email = email.trim();
      if (role !== user.role) body.role = role;
      if (Object.keys(body).length === 0) { toast.info("Keine Änderungen."); onClose(); return; }
      await api.patch(`/admin/users/${user.id}`, body);
      toast.success("Admin aktualisiert.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aktualisieren fehlgeschlagen."); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-[#5C6B49] border border-white/15 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-black">Admin bearbeiten</h2>
          <button type="button" onClick={onClose} className="p-1 text-[#C9C6B4] hover:text-[#F3F1E8]"><X size={18} /></button>
        </div>
        <p className="text-sm text-[#C9C6B4] mb-3">Benutzer: <strong className="text-[#F3F1E8]">{user.username}</strong></p>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] mb-1 block">E-Mail-Adresse</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="edit-email" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] mb-1 block">Rolle</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-[#4E5A3B] border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="edit-role">
              <option value="admin">Admin</option>
              <option value="master_admin">Master-Admin</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 hover:bg-black/[0.15] text-sm font-bold rounded-lg px-4 py-2">Abbrechen</button>
          <button type="submit" disabled={loading} className="flex-1 bg-[#E8E5D0] text-[#3A4230] text-sm font-bold rounded-lg px-4 py-2 disabled:opacity-50" data-testid="edit-submit">{loading ? "…" : "Speichern"}</button>
        </div>
      </form>
    </div>
  );
};

const SetPasswordModal = ({ api, user, handleAuthErr, onClose, onDone }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.trim().length < 8) { toast.error("Mindestens 8 Zeichen."); return; }
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/set-password`, { new_password: pw });
      toast.success("Neues Passwort gesetzt. Bestehende Sitzungen wurden abgemeldet.");
      onDone();
    } catch (e) { if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-[#5C6B49] border border-white/15 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="setpw-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-black">Passwort setzen</h2>
          <button type="button" onClick={onClose} className="p-1 text-[#C9C6B4] hover:text-[#F3F1E8]"><X size={18} /></button>
        </div>
        <p className="text-sm text-[#C9C6B4] mb-3">Für: <strong className="text-[#F3F1E8]">{user.username}</strong></p>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} placeholder="Neues Passwort (mind. 8 Zeichen)" autoComplete="new-password"
          className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] placeholder:text-[#A8A58F]" data-testid="setpw-input" />
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 hover:bg-black/[0.15] text-sm font-bold rounded-lg px-4 py-2">Abbrechen</button>
          <button type="submit" disabled={loading} className="flex-1 bg-[#E8E5D0] text-[#3A4230] text-sm font-bold rounded-lg px-4 py-2 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="setpw-submit">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Speichern
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminUsers;
